import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  DrivingData,
  Schedule,
  ScheduleData,
  StationData,
  Track,
  TrackInSchedule,
  Vehicle
} from 'src/app/main/shared/DataTypes';
import { ShareDataService } from 'src/app/main/shared/services/share-data.service';
import { SimulationService } from 'src/app/main/shared/services/simulation.service';
import { environment } from 'src/environments/environment';

const SCHEDULE_CSV_COLUMNS = [
  'schedule_name',
  'schedule_version',
  'track_id',
  'track_name',
  'station_name',
  'do_not_stop',
  'stopping_time_s',
  'stopping_actions',
  'driving_time_s',
  'driving_actions'
];

type ParsedScheduleCSVRow = {
  scheduleName: string;
  scheduleVersion: string;
  rowNumber: number;
  station: StationData;
  drivingTime: number | undefined;
  drivingActions: string[];
};

export type ScheduleRenamePreview = {
  variantCount: number;
};

export type ScheduleRenameResponse = {
  changed: boolean;
  oldName: string;
  newName: string;
  oldVersion: string;
  newVersion: string;
  oldDescription: string;
  newDescription: string;
  schedulesUpdated: number;
};

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {
  http = inject(HttpClient);
  shareDataService = inject(ShareDataService);
  simulationService = inject(SimulationService);

  /*
  Returns tracks for a specific schedule from the database according to the defined Data Types
*/
  getScheduleTracksFromDB(schedule: Schedule): Observable<TrackInSchedule[]> {
    const path = `${environment.apiURL}/schedules/${schedule.id}`;
    return this.http.get<{ driving_data: any[]; station_data: any[] }>(path).pipe(
      map(data => {
        const tracksInSchedule: TrackInSchedule[] = [];

        // Process driving data
        data.driving_data.forEach((drivingData: any) => {
          const drivingDataObj: DrivingData = {
            trackId: drivingData.track_id,
            trackName: drivingData.track_name,
            fromStation: drivingData.from_station,
            toStation: drivingData.to_station,
            drivingTime: drivingData.driving_time_in_seconds,
            drivingActions: drivingData.driving_actions
          };

          // Find or create the corresponding TrackInSchedule
          const existingTrackIndex = tracksInSchedule.findIndex(
            track => track.scheduleData.drivingData[0].trackId === drivingDataObj.trackId
          );
          if (existingTrackIndex === -1) {
            tracksInSchedule.push({
              scheduleData: {
                drivingData: [drivingDataObj],
                stoppingData: []
              }
            });
          } else {
            tracksInSchedule[existingTrackIndex].scheduleData.drivingData.push(drivingDataObj);
          }
        });

        // Process station data
        data.station_data.forEach((stationData: any) => {
          const stationDataObj: StationData = {
            trackId: stationData.track_id,
            trackName: stationData.track_name,
            stationName: stationData.station_name,
            doNotStopAtStation: stationData.do_not_stop_at_station,
            stoppingTime: stationData.stopping_time_in_seconds,
            stoppingActions: stationData.stopping_actions
          };

          // Find the corresponding TrackInSchedule and add the stationData
          const existingTrackIndex = tracksInSchedule.findIndex(
            track => track.scheduleData.drivingData[0].trackId === stationDataObj.trackId
          );
          if (existingTrackIndex !== -1) {
            tracksInSchedule[existingTrackIndex].scheduleData.stoppingData.push(stationDataObj);
          }
        });

        return tracksInSchedule;
      })
    );
  }

  saveNewScheduleToDB(schedule: Schedule, workspaceId: number): Observable<string> {
    const path = '/schedules';
    const completeScheduleData = this.simulationService.getCompleteScheduleDataFromScheduleTracks(
      schedule.tracks,
      false
    );
    const formattedScheduleData = this.getFormattedScheduleDataToSnakeCase(completeScheduleData);

    const tracksInSchedule = new Set<number>();
    schedule.tracks.forEach(trackInSchedule => {
      // Get IDs from stopping data
      trackInSchedule.scheduleData.stoppingData.forEach(stopping => {
        if (stopping.trackId) {
          tracksInSchedule.add(stopping.trackId);
        }
      });
    });
    const tracksInScheduleArray = Array.from(tracksInSchedule);
    const payload = {
      schedule: {
        name: schedule.name,
        description: schedule.description,
        version: schedule.version,
        data: formattedScheduleData
      },
      workspaceId: workspaceId,
      trackIds: tracksInScheduleArray
    };
    // Return Schedule Id
    return this.http.post<string>(environment.apiURL + path, payload);
  }

  previewRenameSchedule(oldName: string): Observable<ScheduleRenamePreview> {
    const params = new HttpParams().set('oldName', oldName);
    return this.http.get<ScheduleRenamePreview>(environment.apiURL + '/schedules/rename-preview', {
      params
    });
  }

  renameSchedule(
    scheduleId: number,
    newName: string,
    newVersion: string,
    newDescription: string
  ): Observable<ScheduleRenameResponse> {
    return this.http.patch<ScheduleRenameResponse>(environment.apiURL + '/schedules/rename', {
      scheduleId: scheduleId,
      newName: newName,
      newVersion: newVersion,
      newDescription: newDescription
    });
  }

  getFormattedScheduleDataToSnakeCase(scheduleData: ScheduleData): any {
    const formattedScheduleData: { driving_data: any[]; station_data: any[] } = {
      driving_data: [],
      station_data: []
    };
    scheduleData.drivingData.forEach((drivingData: DrivingData) => {
      const formattedDrivingData = {
        track_id: drivingData.trackId,
        track_name: drivingData.trackName,
        from_station: drivingData.fromStation,
        to_station: drivingData.toStation,
        driving_time_in_seconds: drivingData.drivingTime,
        driving_actions: drivingData.drivingActions
      };
      formattedScheduleData.driving_data.push(formattedDrivingData);
    });
    scheduleData.stoppingData.forEach((stationData: StationData) => {
      const formattedStationData = {
        track_id: stationData.trackId,
        track_name: stationData.trackName,
        station_name: stationData.stationName,
        stopping_time_in_seconds: stationData.stoppingTime,
        stopping_actions: stationData.stoppingActions,
        do_not_stop_at_station: stationData.doNotStopAtStation
      };
      formattedScheduleData.station_data.push(formattedStationData);
    });
    return formattedScheduleData;
  }

  /*
  getManuallyChangedTrackData(tracks: TrackInSchedule[]): {
    [key: string]: TrackDataSet[];
  } {
    const parameters: { [key: string]: TrackDataSet[] } = {};
    for (const [trackIndex, track] of tracks.entries()) {
      // Check if track has manually changed data
      if (track.trackParameters) {
        for (const parameterKey in track.trackParameters) {
          const existingParameterData = parameters[parameterKey] || [];
          const newParameters = track.trackParameters[parameterKey];

          // Find the last position in the existing parameters
          const lastPosition =
            existingParameterData.length > 0
              ? (existingParameterData[existingParameterData.length - 1][
                  'position'
                ] as number)
              : 0;

          // Skip the first data entry in the new parameters if it's not the first track
          const updatedParameters = [
            ...existingParameterData.filter(
              param => (param['position'] as number) <= lastPosition
            ),
            ...newParameters.slice(trackIndex === 0 ? 0 : 0).map(param => ({
              ...param,
              position: (param['position'] as number) + lastPosition,
            })),
          ];

          parameters[parameterKey] = updatedParameters;
        }
      }
    }
    return parameters;
  }
  */

  getScheduleFromTrack(track: Track): Schedule {
    const schedule: Schedule = {
      name: track.name,
      deleted: false,
      tracks: [this.getScheduleTracksFromTrackVariant(track)],
      tags: []
    };
    return schedule;
  }

  getScheduleFromJSON(scheduleImportData: unknown): Schedule {
    if (!this.isValidScheduleImportData(scheduleImportData)) {
      throw new Error('Invalid schedule JSON schema');
    }

    return scheduleImportData.schedule;
  }

  getVehicleFromJSON(scheduleImportData: unknown): Vehicle | undefined {
    if (!this.isValidScheduleImportData(scheduleImportData)) {
      throw new Error('Invalid schedule JSON schema');
    }

    return scheduleImportData.vehicle;
  }

  private isValidScheduleImportData(value: unknown): value is {
    schedule: Schedule;
    vehicle?: Vehicle;
  } {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const payload = value as Record<string, unknown>;

    if (!('schedule' in payload)) {
      return false;
    }

    const schedule = payload['schedule'];
    if (!schedule || typeof schedule !== 'object') {
      return false;
    }

    const scheduleData = schedule as Record<string, unknown>;
    if (typeof scheduleData['name'] !== 'string') {
      return false;
    }

    if (!Array.isArray(scheduleData['tracks'])) {
      return false;
    }

    if (!Array.isArray(scheduleData['tags'])) {
      return false;
    }

    if (typeof scheduleData['deleted'] !== 'boolean') {
      return false;
    }

    return true;
  }

  getScheduleTracksFromTrackVariant(track: Track): TrackInSchedule {
    const scheduleData: ScheduleData = {
      drivingData: [],
      stoppingData: []
    };

    for (let i = 0; i < track.parameters['station_data'].length; i++) {
      const station = track.parameters['station_data'][i];
      const stationData: StationData = {
        trackId: track.id ?? undefined,
        trackName: track.name,
        stationName: station.trackParameter1.toString(),
        doNotStopAtStation: false,
        stoppingTime: 30,
        stoppingActions: []
      };
      scheduleData.stoppingData.push(stationData);

      if (track.parameters['station_data'][i + 1] !== undefined) {
        const drivingData: DrivingData = {
          trackId: track.id ?? undefined,
          trackName: track.name,
          fromStation: station.trackParameter1.toString(),
          toStation: track.parameters['station_data'][i + 1].trackParameter1.toString(),
          drivingTime: 30,
          drivingActions: []
        };
        scheduleData.drivingData.push(drivingData);
      }
    }
    const trackInSchedule: TrackInSchedule = {
      scheduleData: scheduleData
    };
    if (!track.id) {
      // If track has manually changed data, add the data to it so it gets used when simulating
      trackInSchedule.trackParameters = track.parameters;
    }
    return trackInSchedule;
  }

  /*
    Serializes a schedule to CSV: one row per station, with the driving leg leading to the
    next station folded into the same row. The driving columns are empty for the last
    station of a track, since there are always n stations and n-1 driving segments.
  */
  getCSVFromSchedule(schedule: Schedule): string {
    const rows: string[][] = [SCHEDULE_CSV_COLUMNS];

    schedule.tracks.forEach((trackInSchedule: TrackInSchedule) => {
      const stoppingData = trackInSchedule.scheduleData.stoppingData;
      const drivingData = trackInSchedule.scheduleData.drivingData;

      stoppingData.forEach((station: StationData, index: number) => {
        const drivingLeg: DrivingData | undefined = drivingData[index];
        rows.push([
          schedule.name,
          schedule.version ?? '',
          station.trackId?.toString() ?? '',
          station.trackName,
          station.stationName,
          station.doNotStopAtStation ? 'true' : 'false',
          station.stoppingTime.toString(),
          station.stoppingActions.join('|'),
          drivingLeg ? drivingLeg.drivingTime.toString() : '',
          drivingLeg ? drivingLeg.drivingActions.join('|') : ''
        ]);
      });
    });

    return rows.map(row => row.map(cell => this.toCSVCell(cell)).join(',')).join('\r\n');
  }

  getScheduleFromCSV(csvText: string): Schedule {
    const csvWithoutBom = csvText.startsWith('\uFEFF') ? csvText.slice(1) : csvText;
    const rows = this.parseCSV(csvWithoutBom).map(row =>
      row.map(cell => this.restoreCSVCell(cell))
    );
    const header = rows[0];

    if (
      header?.length !== SCHEDULE_CSV_COLUMNS.length ||
      header?.some((column, index) => column !== SCHEDULE_CSV_COLUMNS[index]) === true
    ) {
      throw new Error(`Invalid CSV header: expected ${SCHEDULE_CSV_COLUMNS.join(',')}`);
    }

    const dataRows = rows.slice(1);
    if (dataRows.length === 0) {
      throw new Error('CSV must contain at least one data row');
    }

    const parsedRows = dataRows.map((row, index) => this.parseScheduleCSVRow(row, index + 2));
    const tracks: TrackInSchedule[] = [];
    const scheduleName = parsedRows[0].scheduleName;
    const scheduleVersion = parsedRows[0].scheduleVersion;
    let trackRows: ParsedScheduleCSVRow[] = [];

    for (const row of parsedRows) {
      if (row.scheduleName !== scheduleName || row.scheduleVersion !== scheduleVersion) {
        throw new Error(`Inconsistent schedule name or version on row ${row.rowNumber}`);
      }

      trackRows.push(row);
      if (row.drivingTime === undefined && row.drivingActions.length === 0) {
        tracks.push(this.createTrackFromCSVRows(trackRows));
        trackRows = [];
      }
    }

    if (trackRows.length > 0) {
      const lastRow = trackRows[trackRows.length - 1];
      throw new Error(
        `Invalid driving data on row ${lastRow.rowNumber}: the last station of a track cannot have a driving leg`
      );
    }

    return {
      id: undefined,
      name: scheduleName,
      version: scheduleVersion,
      deleted: false,
      tracks,
      tags: []
    };
  }

  private createTrackFromCSVRows(trackRows: ParsedScheduleCSVRow[]): TrackInSchedule {
    if (trackRows.length < 2) {
      throw new Error(
        `Invalid track ending on row ${trackRows[0].rowNumber}: expected two stations`
      );
    }

    const trackId = trackRows[0].station.trackId;
    const trackName = trackRows[0].station.trackName;
    const inconsistentRow = trackRows.find(
      row => row.station.trackId !== trackId || row.station.trackName !== trackName
    );
    if (inconsistentRow) {
      throw new Error(`Inconsistent track ID or name on row ${inconsistentRow.rowNumber}`);
    }

    const drivingData = trackRows.slice(0, -1).map((row, index): DrivingData => {
      if (row.drivingTime === undefined) {
        throw new Error(
          `Invalid driving_time_s on row ${row.rowNumber}: expected a finite non-negative number`
        );
      }

      return {
        trackId,
        trackName,
        fromStation: row.station.stationName,
        toStation: trackRows[index + 1].station.stationName,
        drivingTime: row.drivingTime,
        drivingActions: row.drivingActions
      };
    });

    return {
      scheduleData: {
        stoppingData: trackRows.map(row => row.station),
        drivingData
      }
    };
  }

  private toCSVCell(value: string): string {
    // Prefix cells that a spreadsheet application would evaluate as a formula
    const sanitizedValue = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
    return `"${sanitizedValue.replace(/"/g, '""')}"`;
  }

  private parseScheduleCSVRow(row: string[], rowNumber: number): ParsedScheduleCSVRow {
    if (row.length !== SCHEDULE_CSV_COLUMNS.length) {
      throw new Error(
        `Invalid CSV row ${rowNumber}: expected ${SCHEDULE_CSV_COLUMNS.length} columns, got ${row.length}`
      );
    }

    const trackId = this.parsePositiveInteger(row[2], 'track_id', rowNumber);
    const doNotStopAtStation = this.parseBoolean(row[5], 'do_not_stop', rowNumber);

    let drivingTime: number | undefined;
    if (row[8].trim() !== '' || row[9].trim() !== '') {
      drivingTime = this.parseNonNegativeNumber(row[8], 'driving_time_s', rowNumber);
    }

    return {
      scheduleName: row[0],
      scheduleVersion: row[1],
      rowNumber,
      station: {
        trackId,
        trackName: row[3],
        stationName: row[4],
        doNotStopAtStation,
        stoppingTime: this.parseNonNegativeNumber(row[6], 'stopping_time_s', rowNumber),
        stoppingActions: this.parseActions(row[7])
      },
      drivingTime,
      drivingActions: this.parseActions(row[9])
    };
  }

  private parsePositiveInteger(value: string, column: string, rowNumber: number): number {
    const parsedValue = Number(value);
    if (
      value.trim() === '' ||
      !Number.isFinite(parsedValue) ||
      !Number.isInteger(parsedValue) ||
      parsedValue <= 0
    ) {
      throw new Error(`Invalid ${column} on row ${rowNumber}: expected a positive integer`);
    }
    return parsedValue;
  }

  private parseNonNegativeNumber(value: string, column: string, rowNumber: number): number {
    const parsedValue = Number(value);
    if (value.trim() === '' || !Number.isFinite(parsedValue) || parsedValue < 0) {
      throw new Error(
        `Invalid ${column} on row ${rowNumber}: expected a finite non-negative number`
      );
    }
    return parsedValue;
  }

  private parseBoolean(value: string, column: string, rowNumber: number): boolean {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue !== 'true' && normalizedValue !== 'false') {
      throw new Error(
        `Invalid ${column} on row ${rowNumber}: expected true or false, got "${value}"`
      );
    }
    return normalizedValue === 'true';
  }

  private parseActions(value: string): string[] {
    return value
      .split('|')
      .map(action => action.trim())
      .filter(action => action !== '');
  }

  private restoreCSVCell(value: string): string {
    return /^'(?=[=+\-@\t\r])/.test(value) ? value.slice(1) : value;
  }

  private parseCSV(csvText: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let inQuotes = false;
    let afterClosingQuote = false;

    for (let index = 0; index < csvText.length; index++) {
      const character = csvText[index];

      if (inQuotes) {
        if (character === '"') {
          if (csvText[index + 1] === '"') {
            cell += '"';
            index++;
          } else {
            inQuotes = false;
            afterClosingQuote = true;
          }
        } else {
          cell += character;
        }
        continue;
      }

      if (afterClosingQuote) {
        if (character === ',') {
          row.push(cell);
          cell = '';
          afterClosingQuote = false;
        } else if (character === '\r' || character === '\n') {
          row.push(cell);
          rows.push(row);
          row = [];
          cell = '';
          afterClosingQuote = false;
          if (character === '\r' && csvText[index + 1] === '\n') {
            index++;
          }
        } else {
          throw new Error('Invalid CSV: unexpected characters after a quoted cell');
        }
        continue;
      }

      if (character === '"') {
        if (cell !== '') {
          throw new Error('Invalid CSV: quoted cell must start at the beginning of a cell');
        }
        inQuotes = true;
      } else if (character === ',') {
        row.push(cell);
        cell = '';
      } else if (character === '\r' || character === '\n') {
        row.push(cell);
        rows.push(row);
        row = [];
        cell = '';
        if (character === '\r' && csvText[index + 1] === '\n') {
          index++;
        }
      } else {
        cell += character;
      }
    }

    if (inQuotes) {
      throw new Error('Invalid CSV: unterminated quoted cell');
    }

    if (afterClosingQuote || cell !== '' || row.length > 0) {
      row.push(cell);
      rows.push(row);
    }

    return rows;
  }

  getDRIVEFileFromSchedule(schedule: Schedule): Observable<any> {
    const url = environment.apiURL + '/drive-exports';
    const payload = this.simulationService.mapScheduleToSimulationData(schedule);

    return this.http.post(url, payload, {
      responseType: 'blob'
    });
  }

  getFirstTenEntriesFromStationDB(searchString: string): Observable<any> {
    const url = environment.apiURL + '/stations/search';
    return this.http.get(url, { params: { searchString: searchString } });
  }

  markTrackInScheduleAsSaved(newTrack: Track, trackInSchedule: TrackInSchedule): TrackInSchedule {
    trackInSchedule.trackParameters = undefined; // Clear the track parameters to indicate it has been saved
    // Add the ID and name to the driving data
    trackInSchedule.scheduleData.drivingData.forEach(drivingData => {
      drivingData.trackId = newTrack.id!;
      drivingData.trackName = newTrack.name;
    });
    // Add the ID and name to the stopping data
    trackInSchedule.scheduleData.stoppingData.forEach(stationData => {
      stationData.trackId = newTrack.id!;
      stationData.trackName = newTrack.name;
    });
    return trackInSchedule;
  }
}
